import {
  BILLION_VALUE_CONVERT_NUM,
  MILLION_VALUE_CONVERT_NUM,
  REGEX_FOR_ABBREVIATE,
  REGEX_FOR_COMMA_SEPARATED_VALUE,
  REGEX_FOR_DECIMAL,
  REGEX_FOR_WHOLE_NUMBER,
  THOUSAND_VALUE_CONVERT_NUM
} from 'src/constants';
import { compile } from 'expression-eval';
import { functionLibrary } from '../../../../../lib';
import { numberWithCommasWithoutDecimal } from '../../simulation-summary/utils/utils';
import { Header } from '../types/generic-table.model';
import { IDataRow } from '../types/simulation-view-table.model';

export function calculatePercentageChange(
  newValue: number,
  initialValue: number
) {
  const change = (newValue / initialValue) * 100 - 100;
  return Math.round(change * 100) / 100;
}

export function calculateAbsoluteChange(
  percentageChange: number,
  initialValue: number
) {
  const change = (percentageChange / 100) * initialValue + initialValue;
  return Math.round(change * 100) / 100;
}

export function calculateValueFromFormula(
  formula: string,
  rowToUpdate: IDataRow,
  fields: string[]
): number {
  const cleanedUpFormula = formula.replaceAll('{', '').replaceAll('}', '');
  const fieldMetadata = fields
    .map((field) => ({ [field]: rowToUpdate[field] }))
    .reduce(
      (previousValue, currentValue) => ({ ...previousValue, ...currentValue }),
      {
        ...functionLibrary
      }
    );
  try {
    return compile(cleanedUpFormula)(fieldMetadata);
  } catch {
    console.error('Error while compiling expresison ', formula, fieldMetadata);
    return 0;
  }
}

export function convertNumberInAbbreviateFormat(
  abbreviateRegexData: RegExpExecArray,
  value: number
) {
  let formattedValue = null;
  if (abbreviateRegexData.filter((data) => data === ',').length) {
    formattedValue = value / THOUSAND_VALUE_CONVERT_NUM;
  } else if (abbreviateRegexData.filter((data) => data === ',,').length) {
    formattedValue = value / MILLION_VALUE_CONVERT_NUM;
  } else if (abbreviateRegexData.filter((data) => data === ',,,').length) {
    formattedValue = value / BILLION_VALUE_CONVERT_NUM;
  }
  return formattedValue;
}

export const getRegexInstance = (regexString: string) =>
  new RegExp(regexString, 'g');

export const execRegexInstance = (
  regexInstance: RegExp,
  numberFormat: string
) => regexInstance.exec(numberFormat);

const getUpdatedValue = (value: number, updatedValue: number) =>
  updatedValue !== null ? updatedValue : value;

const abbreviateNumberConverter = (
  abbreviateRegexData: RegExpExecArray,
  num: number
) => {
  let formattedValue = null;
  if (abbreviateRegexData.filter((data) => data === ',,,').length) {
    formattedValue = `${(num / BILLION_VALUE_CONVERT_NUM)
      .toFixed(1)
      .replace(/\.0$/, '')}B`;
  } else if (abbreviateRegexData.filter((data) => data === ',,').length) {
    formattedValue = `${(num / MILLION_VALUE_CONVERT_NUM)
      .toFixed(1)
      .replace(/\.0$/, '')}M`;
  } else if (abbreviateRegexData.filter((data) => data === ',').length) {
    formattedValue = `${(num / THOUSAND_VALUE_CONVERT_NUM)
      .toFixed(1)
      .replace(/\.0$/, '')}K`;
  }
  return formattedValue;
};

export function formatNumber(
  format: string,
  value: number,
  prefix = '',
  suffix = ''
) {
  let updatedValue = null;
  const regexForAbbreviate = getRegexInstance(REGEX_FOR_ABBREVIATE);
  const regexForDecimal = getRegexInstance(REGEX_FOR_DECIMAL);
  const regexForCommaSeparator = getRegexInstance(
    REGEX_FOR_COMMA_SEPARATED_VALUE
  );
  const regexForWholeNumPattern = getRegexInstance(REGEX_FOR_WHOLE_NUMBER);

  const abbreviateRegexData = execRegexInstance(regexForAbbreviate, format);
  const decimalRegexData = execRegexInstance(regexForDecimal, format);
  const wholeNumRegexData = execRegexInstance(regexForWholeNumPattern, format);
  const commaSeparatorRegexData = execRegexInstance(
    regexForCommaSeparator,
    format
  );
  if (decimalRegexData) {
    updatedValue = getUpdatedValue(value, updatedValue).toFixed(
      decimalRegexData[decimalRegexData.length - 1].length
    );
  }
  //fix: normalze negetive zero (-0.0 -> 0.0)
  if (updatedValue != null && Number(updatedValue) === 0) {
    updatedValue = updatedValue.replace('-', '');
  }
  if (wholeNumRegexData) {
    const valueSplit = getUpdatedValue(value, updatedValue)
      .toString()
      .split('.');
    updatedValue = +valueSplit[0];
  }
  if (abbreviateRegexData) {
    updatedValue = abbreviateNumberConverter(
      abbreviateRegexData,
      getUpdatedValue(value, updatedValue)
    );
  }
  if (commaSeparatorRegexData) {
    updatedValue = numberWithCommasWithoutDecimal(
      getUpdatedValue(value, updatedValue)
    );
  }

  if (suffix) {
    updatedValue = `${getUpdatedValue(
      value,
      updatedValue
    ).toString()}${suffix}`;
  }
  if (prefix) {
    if (value >= 0) {
      updatedValue = prefix.concat(
        '',
        getUpdatedValue(value, updatedValue).toString()
      );
    } else {
      const val =
        updatedValue && updatedValue.indexOf('-') === 0
          ? updatedValue.split('-')[1]
          : updatedValue;

      updatedValue = `-${prefix.concat(
        '',
        getUpdatedValue(value * -1, val).toString()
      )}`;
    }
  }
  return updatedValue;
}

export function formatNumberUsingRegex(
  columnMetaData: Header,
  value: number | any
) {
  return formatNumber(
    columnMetaData.parent.number_format,
    value,
    columnMetaData.parent.prefix_label,
    columnMetaData.parent.suffix_label
  );
}

// function to extract values from formatted cell value
export const extractValue = (value: string) => {
  // Remove non-numeric characters except for `.` and `-`
  const cleanedValue = value.replace(/[^0-9.-]+/g, '');

  // Parse the cleaned value to a float
  const numericValue =
    parseFloat(cleanedValue) === 0 ? 0 : parseFloat(cleanedValue);

  if (Number.isNaN(numericValue)) return 0;

  return numericValue;
};
